import Experience from "../../Experience"

export default class Logo {
    constructor(container) {
        this.experience = new Experience()
        this.container = container
        this.width  = 280
        this.height = 185 * 1.2125

        this.container.style.cssText = `
            width: ${this.width}rem;
            height: ${this.height}rem;
            position: fixed;
            top: 50rem;
            left: 50rem;
            z-index: 800;
            overflow: visible;
            cursor: pointer;
        `

        this.buildLogo()
        this.buildOverlay()
        this.setMouseEvents()
        this.open()
    }

    buildLogo() {
        this.imgWrapper = document.createElement('div')
        this.imgWrapper.style.cssText = `
            position: absolute;
            top: 0; left: 0;
            width: 100%; height: 100%;
            overflow: hidden;
        `

        this.img = document.createElement('img')
        this.img.src = '/images/brit_logo_white.png'
        // PNG: white shapes, transparent background.
        // Filter mathematically computed to map white → #E2DCBA ≈ #E3DBBB (< 1% error).
        // brightness(0.77): dims white to 77%
        // sepia(0.55): warm yellow-brown shift
        // hue-rotate(14deg): fine-tunes to match the exact cream tone of var(--white)
        this.img.style.cssText = `
            width: 100%;
            height: 100%;
            object-fit: contain;
            display: block;
            transition: transform 0.5s var(--ease);
            filter: brightness(0.77) sepia(0.55) hue-rotate(14deg);
        `

        this.imgWrapper.appendChild(this.img)
        this.container.appendChild(this.imgWrapper)
    }

    buildOverlay() {
        const pad = 24
        const W  = this.width  + pad * 2
        const H  = this.height + pad * 2
        const cx = W / 2
        const cy = H / 2
        const r1 = Math.min(W, H) / 2 - 4
        const r2 = r1 - 14
        const tickLen = 10

        const ticks = [0, 45, 90, 135, 180, 225, 270, 315].map(deg => {
            const rad = deg * Math.PI / 180
            const x1 = (cx + (r1 - tickLen) * Math.cos(rad)).toFixed(1)
            const y1 = (cy + (r1 - tickLen) * Math.sin(rad)).toFixed(1)
            const x2 = (cx + (r1 + 4)       * Math.cos(rad)).toFixed(1)
            const y2 = (cy + (r1 + 4)       * Math.sin(rad)).toFixed(1)
            return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="var(--white)" stroke-width="1.5"/>`
        }).join('')

        const crossLen = r1 * 0.38
        const crossLines = `
            <line x1="${(cx - crossLen).toFixed(1)}" y1="${cy}" x2="${(cx - r2 + 6).toFixed(1)}" y2="${cy}" stroke="var(--white)" stroke-width="1"/>
            <line x1="${(cx + r2 - 6).toFixed(1)}"  y1="${cy}" x2="${(cx + crossLen).toFixed(1)}"  y2="${cy}" stroke="var(--white)" stroke-width="1"/>
            <line x1="${cx}" y1="${(cy - crossLen).toFixed(1)}" x2="${cx}" y2="${(cy - r2 + 6).toFixed(1)}" stroke="var(--white)" stroke-width="1"/>
            <line x1="${cx}" y1="${(cy + r2 - 6).toFixed(1)}"  x2="${cx}" y2="${(cy + crossLen).toFixed(1)}"  stroke="var(--white)" stroke-width="1"/>
        `

        const circ1 = (2 * Math.PI * r1).toFixed(1)
        const circ2 = (2 * Math.PI * r2).toFixed(1)

        const svgNS = 'http://www.w3.org/2000/svg'
        const svg = document.createElementNS(svgNS, 'svg')
        svg.setAttribute('width',   `${W}rem`)
        svg.setAttribute('height',  `${H}rem`)
        svg.setAttribute('viewBox', `0 0 ${W} ${H}`)
        svg.style.cssText = `
            position: absolute;
            top: ${-pad}rem; left: ${-pad}rem;
            pointer-events: none;
            overflow: visible;
        `

        svg.innerHTML = `
            <style>
                .logo-ring {
                    fill: none; stroke: var(--white); stroke-width: 1.2;
                    stroke-dasharray: ${circ1}; stroke-dashoffset: ${circ1};
                    transition: stroke-dashoffset 0.6s var(--ease);
                    transform-origin: ${cx}px ${cy}px; transform: rotate(-90deg);
                }
                .logo-ring-inner {
                    fill: none; stroke: var(--white); stroke-width: 0.8; opacity: 0.5;
                    stroke-dasharray: ${circ2}; stroke-dashoffset: ${circ2};
                    transition: stroke-dashoffset 0.5s var(--ease) 0.1s;
                    transform-origin: ${cx}px ${cy}px; transform: rotate(-90deg);
                }
                .logo-ticks { opacity: 0; transition: opacity 0.25s ease 0.55s; }
                .logo-cross { opacity: 0; transition: opacity 0.25s ease 0.45s; }
            </style>
            <circle class="logo-ring"       cx="${cx}" cy="${cy}" r="${r1}"/>
            <circle class="logo-ring-inner" cx="${cx}" cy="${cy}" r="${r2}"/>
            <g class="logo-ticks">${ticks}</g>
            <g class="logo-cross">${crossLines}</g>
        `

        this.svg       = svg
        this.ring      = svg.querySelector('.logo-ring')
        this.ringInner = svg.querySelector('.logo-ring-inner')
        this.ticksG    = svg.querySelector('.logo-ticks')
        this.crossG    = svg.querySelector('.logo-cross')
        this.circ1     = parseFloat(circ1)
        this.circ2     = parseFloat(circ2)

        this.container.appendChild(svg)
    }

    _showOverlay() {
        this.ring.style.strokeDashoffset      = '0'
        this.ringInner.style.strokeDashoffset = '0'
        this.ticksG.style.opacity = '1'
        this.crossG.style.opacity = '1'
        this.img.style.transform  = 'scale(1.05)'
    }

    _hideOverlay() {
        this.ring.style.strokeDashoffset      = `${this.circ1}`
        this.ringInner.style.strokeDashoffset = `${this.circ2}`
        this.ticksG.style.transition = 'opacity 0.12s ease 0s'
        this.crossG.style.transition = 'opacity 0.12s ease 0s'
        this.ticksG.style.opacity = '0'
        this.crossG.style.opacity = '0'
        setTimeout(() => {
            this.ticksG.style.transition = 'opacity 0.25s ease 0.55s'
            this.crossG.style.transition = 'opacity 0.25s ease 0.45s'
        }, 150)
        this.img.style.transform = 'scale(1)'
    }

    setMouseEvents() {
        this.container.addEventListener('mouseenter', () => this._showOverlay())
        this.container.addEventListener('mouseleave', () => this._hideOverlay())
        this.container.addEventListener('click', (e) => {
            e.preventDefault()
            e.stopPropagation()
            this.experience.router.route(e)
        })
    }

    open() {
        this.container.style.pointerEvents = 'all'
        this.imgWrapper.style.transition  = 'none'
        this.imgWrapper.style.clipPath    = 'inset(50% 0 50% 0)'
        void this.imgWrapper.offsetHeight
        this.imgWrapper.style.transition  = 'clip-path 1s var(--ease)'
        this.imgWrapper.style.clipPath    = 'inset(0% 0 0% 0)'
    }

    close() {
        this._hideOverlay()
        this.container.style.pointerEvents = 'none'
        this.imgWrapper.style.transition = 'none'
        this.imgWrapper.style.clipPath   = 'inset(0% 0 0% 0)'
        void this.imgWrapper.offsetHeight
        this.imgWrapper.style.transition = 'clip-path 1s var(--ease)'
        this.imgWrapper.style.clipPath   = 'inset(50% 0 50% 0)'
    }
}
